import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import BenchlingConfig from 'BenchlingConfig';
import {TagUpdateOptions} from 'file-browser/FinderActions';
import {Tag} from 'folder-items/FileConstants';
import MetadataTagTable from 'metadata/MetadataTagTable';
import {isFixedPlate} from 'samples/SampleUtil';
import {StorableSourceSelectorWithTitle} from 'samples/storables/StorableSourceSelector';
import {getTagTableFriendlyTags} from 'schemas/tags/TagUtil';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {Title} from 'ui-library/Headers';
import APIUtil from 'util/APIUtil';
import {capitalize, typeToName} from 'util/NameUtil';

import * as StorableActions from '../StorableActions';
import {GridTabChildProps} from './GridContainer';
import GridHeader from './GridHeader';
import {getNumAvailableGridCells, getTotalGridCapacity} from './GridUtil';

interface GridMetadataProps extends GridTabChildProps {}

/**
 * Metadata tab for a grid entity which displays schema information and options to edit grid metadata.
 */
export default function GridMetadata({
  grid,
  folderId,
  parentStorable,
}: GridMetadataProps): JSX.Element | null {
  const isWritable = grid.permissions.writable;
  const tagTableFriendlyTags = getTagTableFriendlyTags(grid.tags);
  const gridWord = typeToName(APIUtil.apiIdToType(grid.id));

  async function handleProjectChange(sourceId: string): Promise<void> {
    await StorableActions.updateGridWithDataMods(grid.id, folderId, {sourceId});
  }

  async function handleUpdateTags(
    tagUpdates: TagUpdateOptions
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> {
    const {toSet} = tagUpdates;
    const updatedGrid = await StorableActions.updateGridWithDataMods(grid.id, folderId, {
      tags: toSet,
    });
    return (
      updatedGrid && [
        {
          id: updatedGrid.id,
          tags: updatedGrid?.tags
            ? getTagTableFriendlyTags(updatedGrid.tags)
            : getTagTableFriendlyTags(grid.tags),
        },
      ]
    );
  }

  return (
    <div className={css(styles.metadataTab)} {...getTestProps({component: 'GridMetadata', element: 'root'})}>
      <GridHeader folderId={folderId} grid={grid} parentStorable={parentStorable} />
      <Row className="u-spacing-top" dataTestAttrs={{component: 'GridMetadata', element: 'schemaSection'}}>
        <Col sm={6}>
          <Title>Schema</Title>
          <div>{getArchiveAwareDisplayName(grid.storableSchema)}</div>
        </Col>
        <Col sm={6}>
          <StorableSourceSelectorWithTitle
            isWritable={isWritable}
            registryId={grid.storableSchema.registryId}
            onChange={handleProjectChange}
            selectedSourceId={grid.sourceId}
          />
        </Col>
      </Row>
      {!isFixedPlate(grid) && (
        <Row
          className="u-spacing-top"
          dataTestAttrs={{component: 'GridMetadata', element: 'availableSpaceSection'}}
        >
          <Col sm={6}>
            <Title>Available space</Title>
            <div {...getTestProps({component: 'GridMetadata', element: 'availableSpaceValue'})}>
              {`${getNumAvailableGridCells(grid)} / ${getTotalGridCapacity(grid)}`}
            </div>
          </Col>
        </Row>
      )}
      {BenchlingConfig.STUDIES_ENABLE && isItemStudyable(grid) && (
        <Row className="u-spacing-top" dataTestAttrs={{component: 'GridMetadata', element: 'studiesSection'}}>
          <Col sm={6}>
            <Title>Studies</Title>
            <EditableStudiesList studyable={grid} isWritable={grid.permissions.writable} />
          </Col>
        </Row>
      )}
      <div className="u-spacing-top">
        <MetadataTagTable
          emptyMessage={`This ${gridWord} has no schema fields.`}
          fileId={grid.id}
          itemLinkAllowed={false}
          initialTags={tagTableFriendlyTags}
          areTagsEditable={isWritable}
          title={`${capitalize(gridWord)} field`}
          updateTags={handleUpdateTags}
          showRequiredFieldErrors={true}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  metadataTab: {maxWidth: 828, margin: 'auto'},
});
